import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Clock, Trash2, Calendar } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const BADGE = {
  pending: { label: "Pending review", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Approved · live", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Needs changes", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

const BLANK = { name: "", price: "", total_inventory: "", drops_at: "", ends_at: "", description: "", story: "", image_url: "" };

export default function DropsTab({ approved }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/farm/drops").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name, description: form.description, story: form.story, image_url: form.image_url,
        price: parseFloat(form.price), total_inventory: parseInt(form.total_inventory, 10),
        drops_at: form.drops_at ? new Date(form.drops_at).toISOString() : "",
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      await api.post("/farm/drops", payload);
      setShowForm(false); setForm(BLANK); load();
    } catch (e) { setErr(e.response?.data?.detail || "Submit failed"); } finally { setBusy(false); }
  };

  const withdraw = async (slug) => {
    if (!window.confirm("Withdraw this drop request?")) return;
    await api.delete(`/farm/drops/${slug}`); load();
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric" }) : "—");

  return (
    <div className="space-y-6" data-testid="farm-drops-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl">Your limited drops</h3>
          <p className="text-sm text-[var(--ff-text-muted)]">Propose and schedule your own limited drops. Our team reviews each one before it goes live to members.</p>
        </div>
        {approved && <button onClick={() => { setForm(BLANK); setShowForm(true); }} className="btn-primary" data-testid="drops-request-btn"><Plus size={16} /> Request a drop</button>}
      </div>

      {!approved && <div className="bg-amber-50 border border-amber-200 p-4 rounded text-sm text-amber-800" data-testid="drops-not-approved">Your rancher account must be approved before you can request drops.</div>}

      {rows.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-10 text-center" data-testid="drops-empty">No drops yet. Request your first limited drop above.</p>}

      <div className="space-y-3">
        {rows.map((d) => {
          const st = d.approval_status || "approved";
          const b = BADGE[st] || BADGE.approved;
          return (
            <div key={d.id} className="card-flat p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`farm-drop-${d.slug}`}>
              <div className="flex gap-4">
                {d.image_url && <img src={d.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{d.name}</p>
                    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${b.cls}`} data-testid={`farm-drop-status-${d.slug}`}>{b.label}</span>
                  </div>
                  <p className="text-xs text-[var(--ff-text-muted)] mt-1 flex items-center gap-1"><Calendar size={12} /> Drops {fmt(d.drops_at)}{d.ends_at ? ` · ends ${fmt(d.ends_at)}` : ""}</p>
                  <p className="text-sm mt-1">${Number(d.price).toFixed(2)} · {d.total_inventory} units{d.sold ? ` · ${d.sold} sold` : ""}</p>
                  {st === "rejected" && d.review_notes && <p className="text-xs text-rose-700 mt-1">Notes: {d.review_notes}</p>}
                </div>
              </div>
              {st !== "approved" && (
                <button onClick={() => withdraw(d.slug)} className="text-xs text-rose-600 flex items-center gap-1 hover:underline" data-testid={`farm-drop-withdraw-${d.slug}`}><Trash2 size={12} /> Withdraw</button>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4 my-8" onClick={(e) => e.stopPropagation()} data-testid="drop-request-modal">
            <h3 className="font-display text-2xl flex items-center gap-2"><Clock size={20} /> Request a drop</h3>
            <p className="text-sm text-[var(--ff-text-muted)]">Fill in the details. Our team reviews and approves before it goes live.</p>
            {err && <p className="text-sm text-rose-700" data-testid="drop-request-error">{err}</p>}
            <label className="block"><span className="overline">Drop name</span>
              <input className="input-base mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="drop-name" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="overline">Price (USD)</span>
                <input type="number" step="0.01" min="0" className="input-base mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="drop-price" /></label>
              <label className="block"><span className="overline">Total units</span>
                <input type="number" min="1" className="input-base mt-1" value={form.total_inventory} onChange={(e) => setForm({ ...form, total_inventory: e.target.value })} data-testid="drop-inventory" /></label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="overline">Drops at</span>
                <input type="datetime-local" className="input-base mt-1" value={form.drops_at} onChange={(e) => setForm({ ...form, drops_at: e.target.value })} data-testid="drop-drops-at" /></label>
              <label className="block"><span className="overline">Ends at (optional)</span>
                <input type="datetime-local" className="input-base mt-1" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="drop-ends-at" /></label>
            </div>
            <label className="block"><span className="overline">Short description</span>
              <input className="input-base mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="drop-description" /></label>
            <label className="block"><span className="overline">Story (optional)</span>
              <textarea rows={3} className="input-base mt-1" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="drop-story" /></label>
            <div><span className="overline">Image</span>
              <div className="mt-1"><ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} folder="drops" testId="drop-image" /></div></div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="button" disabled={busy || !form.name || !form.price || !form.total_inventory || !form.drops_at} onClick={submit} className="btn-primary" data-testid="drop-submit">{busy ? "Submitting…" : "Submit for review"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
